import { Alert, Box, Button, Group, Loader, Popover, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconSparkles } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { sporocilo as sporociloNapake } from '../api/napaka';
import { barve } from '../tema';
import { porabljeno, type Sporocilo } from './asistent';

type Ocena = Shema['AsistentKomentarOcena'];

/**
 * »Komentiraj«: komentar umetne inteligence k številkam kartice, samo na izrecno zahtevo – najprej pokaže okvirno ceno in
 * kaj se pošlje (zbirne številke brez imen oseb), šele potrditev pokliče model. Komentar ni del pogovora z asistentom.
 */
export function GumbKomentarja({
  sporociloId,
  naSpremembo,
}: {
  sporociloId: number;
  naSpremembo: (s: Sporocilo) => void;
}) {
  const odjemalec = useQueryClient();
  const [odprto, setOdprto] = useState(false);
  const [ocena, setOcena] = useState<Ocena | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);

  const odpri = async () => {
    setOdprto(true);
    setOcena(null);
    setNapaka(null);
    try {
      setOcena(
        await podatki(api.GET('/api/asistent/sporocila/{id}/komentar', { params: { path: { id: sporociloId } } })),
      );
    } catch (e) {
      setNapaka(sporociloNapake(e));
    }
  };

  const komentiraj = async () => {
    setDela(true);
    setNapaka(null);
    try {
      const s = await podatki(
        api.POST('/api/asistent/sporocila/{id}/komentar', { params: { path: { id: sporociloId } } }),
      );
      naSpremembo(s);
      void odjemalec.invalidateQueries({ queryKey: ['ai-stanje'] });
      setOdprto(false);
    } catch (e) {
      setNapaka(sporociloNapake(e));
    } finally {
      setDela(false);
    }
  };

  return (
    <Popover opened={odprto} onChange={setOdprto} width={300} position="bottom-end" withArrow shadow="md">
      <Popover.Target>
        <Button
          size="compact-xs"
          variant="light"
          leftSection={<IconSparkles size={14} />}
          onClick={() => (odprto ? setOdprto(false) : void odpri())}
        >
          Komentiraj
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Text size="sm">
            Umetna inteligenca napiše kratek komentar k številkam na tej kartici. Pošljemo ji zbirne številke brez imen
            oseb.
          </Text>
          {!ocena && !napaka && <Loader size="xs" aria-label="Nalagam ceno" />}
          {ocena?.tocke !== undefined && ocena.tocke !== null && (
            <Text size="sm" fw={600}>
              Okvirna cena: {porabljeno(ocena.tocke)}
            </Text>
          )}
          {(ocena?.razlog || napaka) && (
            <Alert
              color={napaka ? 'red' : 'yellow'}
              variant="light"
              p="xs"
              icon={<IconAlertCircle size={16} />}
              role="alert"
            >
              {napaka ?? ocena?.razlog}
            </Alert>
          )}
          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="subtle" onClick={() => setOdprto(false)}>
              Prekliči
            </Button>
            <Button size="xs" onClick={() => void komentiraj()} loading={dela} disabled={!ocena || !!ocena.razlog}>
              Komentiraj
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

/** Komentar umetne inteligence pod kartico (s porabo). */
export function Komentar({ besedilo, zaracunano }: { besedilo: string; zaracunano?: number }) {
  return (
    <Box
      mt="sm"
      px="sm"
      py="xs"
      style={{ background: barve.povrsinaPolje, border: `1px solid ${barve.obroba}`, borderRadius: 10 }}
      aria-label="Komentar umetne inteligence"
    >
      <Group gap={6} mb={4} wrap="nowrap">
        <IconSparkles size={14} color={barve.primarna} />
        <Text size="xs" fw={700}>
          Komentar umetne inteligence
        </Text>
      </Group>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {besedilo}
      </Text>
      <Text size="xs" c="dimmed" mt={4}>
        Komentar je predlog – preverite ga s številkami zgoraj.
        {zaracunano !== undefined && zaracunano !== null && ` Porabljeno: ${porabljeno(zaracunano)}`}
      </Text>
    </Box>
  );
}
